<template>
    <div
        class="dialog-box bg-light mx-auto position-relative py-32 rounded shadow-default overflow-y-auto overflow-x-hidden"
        aria-labelledby="dialog-label"
        aria-modal="true"
        role="dialog"
    >
        <button-link
            v-if="!isRequired"
            class="float-right mr-16"
            icon="close"
            @click="cancel"
        />

        <div class="px-48">
            <icon-circled
                :size="110"
                icon="info"
                :class="$css($theme.textPrimary)"
                class="d-block mx-auto mb-20 mt-32"
            />

            <p
                class="font--20 lh--26 font-weight-light text-center pb-8"
                :class="$css($theme.textPrimary)"
            >
                {{ ucfirst($t("common.alertModal.title")) }}
            </p>

            <validatable-form
                v-slot="{ formFields }"
                class="d-flex flex-column"
                :fields="cookedAlertFields"
                :values="formData"
                @submit="submit"
            >
                <div
                    v-for="({ props, component }, key, idx) in formFields"
                    :key="`input-${idx}`"
                    class="mb-20"
                >
                    <component
                        :is="component"
                        v-bind="props"
                        v-model:value="formData[key]"
                        required
                    >
                        <template
                            v-if="props.tip"
                            #right
                        />
                    </component>
                </div>

                <div class="mt-32 mb-32">
                    <input-checkbox
                        v-if="!isRequired"
                        v-model:value="alertSkipped"
                    >
                        {{ ucfirst($t("common.alertModal.checkBox")) }}
                    </input-checkbox>
                </div>

                <div class="d-flex text-center justify-content-center">
                    <base-button
                        class="w-50"
                        data-t="button-confirm"
                        type="submit"
                    >
                        {{ $t("common.confirm") }}
                    </base-button>
                </div>
            </validatable-form>
        </div>
    </div>
</template>

<script>
import { ucfirst } from "~/lib/utils"
import { KEY_CODES as kcodes } from "~/lib/constants"
import BaseButton from "~/components/buttons/BaseButton"
import ButtonLink from "~/components/buttons/ButtonLink"
import IconCircled from "~/components/elements/IconCircled"
import ValidatableForm from "~/components/ValidatableForm"
import InputCheckbox from "~/components/inputs/InputCheckbox"
import { useListenerManager } from "front-toolkit/plugins/ListenersPlugin"

export default {
    inject: ["popModal"],

    props: {
        alertFields: Array,
        timeZone: {
            type: String,
            default: "Europe/Madrid",
        },
    },

    emits: ["submit"],

    setup() {
        const { listen } = useListenerManager()
        return { listen }
    },

    data() {
        return {
            formData: {},
            alertSkipped: false,
        }
    },

    computed: {
        cookedAlertFields() {
            return this.alertFields.map(field => {
                let { innerLabel = "", required, placeholder = "" } = field
                innerLabel = innerLabel || placeholder
                if (required) {
                    innerLabel = `*${innerLabel}`
                }
                return {
                    ...field,
                    innerLabel,
                }
            })
        },

        isRequired() {
            return this.alertFields.some(field => field.required)
        },
    },

    mounted() {
        this.listen("keydown", event => {
            if (event.keyCode == kcodes.ESC && !this.isRequired) {
                this.popModal()
            }
        })
    },

    created() {
        this.setDefaultValues()
    },

    methods: {
        setDefaultValues() {
            const timezoneField = this.alertFields.find(
                field => field.prop === "timezone"
            )
            if (!timezoneField) return
            const timezoneId = timezoneField.options.find(
                option => option.text === this.timeZone
            )?.value
            if (timezoneId) {
                this.formData.timezone = timezoneId
            }
        },

        async submit() {
            this.$emit("submit", {
                ...this.formData,
                skippedAlert: this.alertSkipped,
            })
        },

        cancel() {
            this.popModal()
        },

        ucfirst,
    },

    components: {
        BaseButton,
        ButtonLink,
        IconCircled,
        ValidatableForm,
        InputCheckbox,
    },
}
</script>

<style scoped>
.dialog-box {
    max-width: 536px;
    top: 5%;
    bottom: 5%;
    max-height: 90%;
}
</style>
